/*
 * Coveo analytics consent handling.
 *
 * The site runs two Coveo stacks and both must be gated on the same
 * OneTrust category:
 *   - Coveo JSUI  (Coveo.init / `.CoveoAnalytics` elements)
 *   - Coveo Headless (buildSearchEngine / buildRecommendationEngine)
 *
 * Consent is opt-in: analytics stays disabled unless C0002 is affirmatively
 * active. Every check must fail closed when the OneTrust globals are missing
 * (OneTrust not loaded yet, or environments without the consent banner).
 */

const CONSENT_LEVEL = 'C0002'

const COVEO_COOKIE_DOMAINS = [undefined, '.dow.com']

const COVEO_COOKIE_NAMES = ['coveo_visitorId', 'coveo_clientId']

const COVEO_STORAGE_KEYS = ['coveo_visitorId', 'coveo_clientId', 'visitorId']

/* Marker class applied to a `.CoveoAnalytics` element held back pre-consent. */
const SUPPRESSED_CLASS = 'dow-coveo-analytics-suppressed'

export const COVEO_ANALYTICS_SELECTOR = `.CoveoAnalytics, .${SUPPRESSED_CLASS}`

/**
 * @returns {boolean} true only when OneTrust reports C0002 as active.
 */
export function hasAnalyticsConsent() {
    const activeGroups =
        window.OnetrustActiveGroups || window.OptanonActiveGroups || ''

    if (typeof activeGroups !== 'string') return false

    return activeGroups.split(',').filter(Boolean).includes(CONSENT_LEVEL)
}

function expireCookie(name, domain, path) {
    let cookie =
        `${encodeURIComponent(name)}=; ` +
        `Path=${path}; ` +
        'Expires=Thu, 01 Jan 1970 00:00:00 GMT; ' +
        'Max-Age=0; ' +
        'SameSite=Lax'

    if (domain) cookie += `; Domain=${domain}`

    document.cookie = cookie
}

function removeStorageKey(storage, key) {
    try {
        storage.removeItem(key)
    } catch (error) {
        console.warn(`Unable to remove Coveo storage key "${key}".`, error)
    }
}

/**
 * Expires the Coveo visitor/client identifiers from cookies and browser
 * storage. Safe to call when no identifier exists.
 *
 * A cookie is only removable when Name, Domain and Path all match the values
 * it was written with, and none of those are readable from `document.cookie`.
 * Every plausible combination is therefore attempted.
 */
export function clearCoveoIdentifiers() {
    const domains = [...COVEO_COOKIE_DOMAINS, window.location.hostname]
    const paths = ['/', window.location.pathname]

    COVEO_COOKIE_NAMES.forEach((cookieName) => {
        domains.forEach((domain) => {
            paths.forEach((path) => expireCookie(cookieName, domain, path))
        })
    })

    COVEO_STORAGE_KEYS.forEach((key) => {
        removeStorageKey(window.localStorage, key)
        removeStorageKey(window.sessionStorage, key)
    })
}

/**
 * Applies the current consent state to every mounted Coveo JSUI analytics
 * component. Call after `afterInitialization` and again on consent change.
 */
export function disableAnalyticsIfNecessary() {
    const userOptedOut = !hasAnalyticsConsent()

    if (userOptedOut) clearCoveoIdentifiers()

    document.querySelectorAll('.CoveoAnalytics').forEach((analyticsElement) => {

        const analytics = window.Coveo?.get?.(analyticsElement)

        if (!analytics) return

        const toggle = userOptedOut ? analytics.disable : analytics.enable

        if (typeof toggle === 'function') {
            toggle.call(analytics)
        } else {
            console.warn(
                `Coveo Analytics ${
                    userOptedOut ? 'disable' : 'enable'
                }() is not available.`
            )
        }
    })
}

/**
 * Exposes the consent helper on `window.dowConsent` and reacts to consent
 * changes made without a page reload.
 *
 * The bridge is required because two Coveo entry points cannot use ES imports:
 *   - the inline OptanonWrapper() script in the page consent template
 *   - the raw ui.apps job-finder clientlibs
 *
 * Safe to call more than once; registration is idempotent.
 */
export function registerCoveoConsentBridge() {
    window.dowConsent = window.dowConsent || {}
    window.dowConsent.updateCoveoAnalytics = disableAnalyticsIfNecessary
    window.dowConsent.hasAnalyticsConsent = hasAnalyticsConsent

    if (!window.dowConsent.__listenerRegistered) {
        window.addEventListener('OneTrustGroupsUpdated', () =>
            disableAnalyticsIfNecessary()
        )
        window.dowConsent.__listenerRegistered = true
    }

    disableAnalyticsIfNecessary()
}

/**
 * Minimal coveo.analytics `IRuntimeEnvironment` that neither persists nor
 * transmits anything.
 *
 * `analytics.enabled: false` only stops Headless from *sending* events; it
 * still constructs the underlying coveo.analytics client, and that client
 * mints a visitor identifier the first time one is requested. coveo.analytics
 * persists it through `runtime.storage`, whose default browser implementation
 * (`CookieAndLocalStorage`) writes `localStorage.visitorId` *and* a cookie
 * named `coveo_` + key — i.e. `coveo_visitorId`. Substituting a null storage
 * is what actually prevents the write; deleting the cookie afterwards only
 * races it.
 *
 * Shape per coveo.analytics `IRuntimeEnvironment`: { storage, client,
 * getClientDependingOnEventType }. Built inline rather than importing
 * `NoopRuntime` so this module takes no direct dependency on a package that is
 * only present transitively via @coveo/headless.
 */
function createNoopAnalyticsRuntime() {
    const client = { sendEvent: () => Promise.resolve() }

    return {
        storage: {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {}
        },
        client,
        getClientDependingOnEventType: () => client
    }
}

/**
 * Builds the `analytics` block for a Headless engine configuration.
 *
 * When consent is absent the analytics client is additionally pinned to the
 * legacy mode and given a no-op runtime, because `runtimeEnvironment` is only
 * honoured by the legacy (coveo.analytics) client. Pinning is safe here: no
 * events are sent in this branch, so the choice of client is unobservable.
 *
 * @param {object} baseConfig caller-supplied analytics options
 * @returns {object} analytics configuration
 */
export function headlessAnalyticsConfig(baseConfig = {}) {
    if (hasAnalyticsConsent()) {
        return { ...baseConfig, enabled: true }
    }

    return {
        ...baseConfig,
        enabled: false,
        runtimeEnvironment: createNoopAnalyticsRuntime()
    }
}

/**
 * Returns a Headless `navigatorContextProvider` that withholds the Coveo
 * client identifier, or `undefined` when consent is present so Headless uses
 * its own default provider.
 *
 * This is the identifier that actually reaches the wire. `engine.navigatorContext`
 * is a getter whose `clientId` is resolved from Relay — *not* from
 * coveo.analytics — so neither `analytics.enabled: false` nor an analytics
 * `runtimeEnvironment` override can suppress it. Resolving it mints and
 * persists the id, which is why `coveo_visitorId` kept reappearing and then
 * being cleared again.
 *
 * `capture: false` is required whenever `clientId` is empty; Coveo rejects
 * requests that pair an empty id with capture enabled.
 *
 * Spread into the engine options next to `configuration`, not inside it.
 *
 * @returns {(function(): object)|undefined} provider, or undefined when consented
 */
export function consentNavigatorContextProvider() {
    if (hasAnalyticsConsent()) return undefined

    return () => ({
        referrer: document.referrer || null,
        userAgent: window.navigator.userAgent || null,
        location: window.location.href || null,
        clientId: '',
        capture: false
    })
}

/**
 * Applies the current consent state to a Coveo Headless engine.
 * @param {object} engine search or recommendation engine
 */
export function applyConsentToHeadlessEngine(engine) {
    if (!engine) return

    if (hasAnalyticsConsent()) {
        if (typeof engine.enableAnalytics === 'function') {
            engine.enableAnalytics()
        }
        return
    }

    if (typeof engine.disableAnalytics === 'function') {
        engine.disableAnalytics()
    }

    clearCoveoIdentifiers()
}
